import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Button, Group, TextInput, Title, Pagination, Badge, ActionIcon, Card, Text, Avatar } from '@mantine/core'
import { IconPlus, IconSearch, IconEdit, IconTrash } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import ConfirmModal from '@/components/ConfirmModal'
import { api, getFileUrl } from '@/lib/api'
import { formatName } from '@/lib/format'

export default function TeacherList() {
  const navigate = useNavigate()
  const [teachers, setTeachers] = useState<Record<string, unknown>[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1 })
  const [search, setSearch] = useState('')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(() => {
    const params: Record<string, string> = { page: String(pagination.page), limit: '20' }
    if (search) params.search = search
    api.admin.getTeachers(params).then((res) => {
      setTeachers(res.data)
      setPagination((p) => ({ ...p, pages: res.pagination.pages }))
    })
  }, [pagination.page, search])

  useEffect(() => { load() }, [load])

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteTeacher(deleteId)
      notifications.show({ title: 'Амжилттай', message: 'Багш устгагдлаа', color: 'green' })
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Устгахад алдаа гарлаа', color: 'red' })
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={2}>Багш нар</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/teachers/new')}>
          Шинэ багш
        </Button>
      </Group>

      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ flex: 1, minWidth: 180 }}
        />
      </Group>

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={800}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Цол</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Чиглэл</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Суралцагч</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Сургалт</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үнэлгээ</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Онцлох</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {teachers.map((t, i) => {
                const user = t.userId as Record<string, unknown> | undefined
                return (
                  <Table.Tr key={t._id as string}>
                    <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Avatar src={getFileUrl(user?.avatar as string)} size={36} radius="xl">{((t.name as string) || formatName(user?.fullName as string))?.charAt(0)}</Avatar>
                        <Text size="sm" fw={500}>{(t.name as string) || formatName(user?.fullName as string) || '-'}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>{t.title as string}</Table.Td>
                    <Table.Td>{t.specialty as string}</Table.Td>
                    <Table.Td fw={500}>{(t.studentCount as number)?.toLocaleString()}</Table.Td>
                    <Table.Td fw={500}>{t.courseCount as number}</Table.Td>
                    <Table.Td fw={500}>{t.rating as number}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={t.isFeatured ? 'blue' : 'gray'}>{t.isFeatured ? 'Тийм' : 'Үгүй'}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6}>
                        <ActionIcon variant="light" color="blue" radius="md" onClick={() => navigate(`/teachers/${t._id}/edit`)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(t._id as string)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
              {teachers.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text ta="center" c="dimmed" py="xl">Багш олдсонгүй</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))} />
          </Group>
        )}
      </Card>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />
    </div>
  )
}
